import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
// Import the thunks from your actions file
import { fetchApplications, downloadApplicationFile, fetchDocuments } from "../../redux/actions/applicationActions"; // Correct path to actions file
import Sidebar from "../../Components/Sidebar";
import { TbArrowBarToDown } from "react-icons/tb";
// Assuming fetchDegreeNames and fetchDepartments are in their own action files/slices
import { fetchDegreeNames } from "../../redux/actions/degreeActions";
import { fetchDepartments } from "../../redux/actions/departmentActions";
import PageComponent from "../../Components/PageComponent";

function Applications() {
  const dispatch = useDispatch();

  const applicationState = useSelector((state) => state.applications.applications);
  const {
    data: applications = [],
    loading,
    error,
    totalPages = 1,
    currentPage = 1,
  } = applicationState;

  // IMPORTANT: Access downloadLoading directly from state.applications
  const downloadLoading = useSelector((state) => state.applications.downloadLoading);

  console.log("totalPages", totalPages); // Debugging

  const degreenames = useSelector((state) => state.degree.degrees.data) || [];
  const departments = useSelector((state) => state.department.department.data) || [];

  const [page, setPage] = useState(currentPage);
  const [itemsPerPage] = useState(5);

  const [formData, setFormData] = useState({
    applicationId: "",
    department: "",
    degree: "",
  });

  useEffect(() => {
    const queryParams = {
      page: page,
      page_size: itemsPerPage,
      ...(formData.applicationId && { application_id: formData.applicationId }),
      ...(formData.department && { department_name: formData.department }),
      ...(formData.degree && { program_name: formData.degree }),
    };
    dispatch(fetchApplications(queryParams));
  }, [dispatch, page, itemsPerPage, formData]);

  useEffect(() => {
    dispatch(fetchDegreeNames());
    dispatch(fetchDepartments());
  }, [dispatch]);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handlePageChange = (e) => {
    setPage(Number(e.target.value));
  };

  // This handles the download of the *main* application file by student_id
  const handleDownloadApplicationFile = (student_id) => {
    dispatch(downloadApplicationFile(student_id));
  };

  // This handles the download of individual documents by file path
  const handleDownloadDocument = (filePath, actualFileName) => {
    dispatch(fetchDocuments(filePath, actualFileName)); // Pass both arguments
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // Helper function to render document download button (MODIFIED)
  const renderDocumentDownloadButton = (documents, docType) => {
    const docArray = documents[docType]; // This should be an array of document objects

    if (docArray && docArray.length > 0) {
      // Determine the action based on the number of files
      const handleButtonClick = () => {
        docArray.forEach(doc => {
          handleDownloadDocument(doc.file_path, doc.actual_file_name);
        });
      };

      // Construct the button title and text based on the number of files
      const buttonTitle = docArray.length > 1
        ? `Download All ${docArray.length} ${docType} Files`
        : `Download ${docArray[0].actual_file_name}`;

      const buttonText = docArray.length > 1
        ? <><TbArrowBarToDown size={20} /> (x{docArray.length})</>
        : <TbArrowBarToDown size={20} />;

      return (
        <button
          onClick={handleButtonClick}
          className="px-3 py-1 text-white rounded-md bg-primary-color hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-color focus:ring-opacity-50"
          title={buttonTitle}
          disabled={downloadLoading} // Disable all download buttons if any download is active
        >
          {buttonText}
        </button>
      );
    }
    return <span className="text-gray-500">N/A</span>;
  };

  const renderPagination = () => {
    const delta = 2; // pages on each side of current
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= page - delta && i <= page + delta)
      ) {
        range.push(i);
      }
    }

    let prev = null;
    for (const i of range) {
      if (prev !== null) {
        if (i - prev === 2) {
          rangeWithDots.push(prev + 1); // single skipped page, show it directly
        } else if (i - prev > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }

    return (
      <div className="flex items-center justify-center mt-4 space-x-1">
        {/* Previous Arrow */}
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          className="flex items-center justify-center w-9 h-9 rounded-md bg-gray-200 text-gray-700 hover:bg-primary-color hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Previous"
        >
          &#8249;
        </button>

        {/* Page Numbers with Dots */}
        {rangeWithDots.map((item, index) =>
          item === "..." ? (
            <span
              key={`dots-${index}`}
              className="flex items-center justify-center w-9 h-9 text-gray-400 select-none"
            >
              &hellip;
            </span>
          ) : (
            <button
              key={item}
              onClick={() => setPage(item)}
              className={`flex items-center justify-center w-9 h-9 rounded-md font-medium transition-colors ${
                page === item
                  ? "bg-primary-color text-white shadow"
                  : "bg-gray-200 text-gray-700 hover:bg-primary-color hover:text-white"
              }`}
            >
              {item}
            </button>
          )
        )}

        {/* Next Arrow */}
        <button
          onClick={() => setPage(page + 1)}
          disabled={page >= totalPages}
          className="flex items-center justify-center w-9 h-9 rounded-md bg-gray-200 text-gray-700 hover:bg-primary-color hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          title="Next"
        >
          &#8250;
        </button>
      </div>
    );
  };

  return (
    <>
      <PageComponent>
        <div className="flex w-screen h-screen bg-[#F8F7FA] overflow-hidden">
          <div className="fixed top-0 left-0 w-64 h-full overflow-y-auto bg-white shadow-md">
            <Sidebar />
          </div>
          <div className="flex-1 min-h-screen p-6 ml-64 overflow-auto">
            {/* Page Title */}
            <div className="flex items-center space-x-2 text-3xl text-gray-500">
              <span>Academics & International</span>
              <span>/</span>
              <span className="text-black">Applications</span>
            </div>

            {/* Filter Form */}
            <div className="w-full p-6 mt-6 bg-white rounded-lg shadow-md">
              <h2 className="mb-4 text-xl font-semibold text-gray-700">Filter All Applications</h2>
              <form>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="applicationId">
                      Application ID
                    </label>
                    <input
                      id="applicationId"
                      name="applicationId"
                      type="text"
                      onChange={handleInputChange}
                      value={formData.applicationId}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                      placeholder="e.g., Reg-000001"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="department">
                      Department
                    </label>
                    <select
                      id="department"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                    >
                      <option value="">All Departments</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.department_name}>
                          {dept.department_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="degree">
                      Degree
                    </label>
                    <select
                      id="degree"
                      name="degree"
                      value={formData.degree}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
                    >
                      <option value="">All Degrees</option>
                      {degreenames.map((degree) => (
                        <option key={degree.id} value={degree.program_name}>
                          {degree.program_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </form>
            </div>

            {/* Applications Table & Pagination */}
            <div className="w-full p-6 mt-4 bg-white rounded-lg shadow-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Applications List</h2>
              </div>

              {/* Status Messages & Table */}
              {loading && <div className="py-4 text-center">Loading applications...</div>}
              {error && <div className="py-4 text-center text-red-600">Error fetching applications: {error}</div>}
              {!loading && !error && (
                <div className="overflow-x-auto max-h-96 sidebar-container">
                   <table className="min-w-full border-collapse table-auto">
                      <thead>
                         <tr className="text-left text-gray-600 bg-gray-100">
                            <th className="px-8 py-2 whitespace-nowrap">APPLICATION ID</th>
                            <th className="px-8 py-2 whitespace-nowrap">DEPARTMENT</th>
                            <th className="px-8 py-2 whitespace-nowrap">DEGREE</th>
                            <th className="px-8 py-2 whitespace-nowrap">APPLICATION DATE</th>
                            <th className="px-8 py-2 whitespace-nowrap">DOWNLOAD APP FILE</th>
                            <th className="px-8 py-2 whitespace-nowrap">NIC</th>
                            <th className="px-8 py-2 whitespace-nowrap">O/L CERTIFICATE</th>
                            <th className="px-8 py-2 whitespace-nowrap">A/L CERTIFICATE</th>
                            <th className="px-8 py-2 whitespace-nowrap">OTHER</th>
                            <th className="px-8 py-2 whitespace-nowrap">PAY SLIP</th>
                         </tr>
                      </thead>
                      <tbody>
                         {applications.length > 0 ? (
                           applications.map((application) => (
                             <tr key={application.application_id} className="border-b border-gray-300 hover:bg-gray-50">
                               <td className="px-8 py-2">{application.application_id}</td>
                               <td className="px-8 py-2">{application.department_name}</td>
                               <td className="px-8 py-2">{application.program_name}</td>
                               <td className="px-8 py-2 whitespace-nowrap">
                                 {application.application_date &&
                                 moment(application.application_date.trim(), "DD-MM-YYYY", true).isValid()
                                   ? moment(application.application_date.trim(), "DD-MM-YYYY").format("D MMM YYYY")
                                   : "N/A"}
                               </td>
                               <td className="px-8 py-2">
                                 <button
                                   onClick={() => handleDownloadApplicationFile(application.student_id)}
                                   className="px-3 py-1 text-white rounded-md bg-primary-color hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary-color focus:ring-opacity-50"
                                   title="Download Application File"
                                   disabled={downloadLoading}
                                 >
                                   {downloadLoading ? "Downloading..." : <TbArrowBarToDown size={20} />}
                                 </button>
                               </td>
                               {/* Individual Document Download Buttons (using modified renderDocumentDownloadButton) */}
                               <td className="px-8 py-2">
                                 {renderDocumentDownloadButton(application.documents, "NIC")}
                               </td>
                               <td className="px-8 py-2">
                                 {renderDocumentDownloadButton(application.documents, "O/L Certificate")}
                               </td>
                               <td className="px-8 py-2">
                                 {renderDocumentDownloadButton(application.documents, "A/L Certificate")}
                               </td>
                               <td className="px-8 py-2">
                                 {renderDocumentDownloadButton(application.documents, "Other Certificates")}
                               </td>
                               <td className="px-8 py-2">
                                 {renderDocumentDownloadButton(application.documents, "Receipt")}
                               </td>
                             </tr>
                           ))
                         ) : (
                           <tr>
                             <td colSpan="10" className="px-8 py-4 text-center text-gray-500">
                               No applications found matching your criteria.
                             </td>
                           </tr>
                         )}
                      </tbody>
                   </table>
                </div>
              )}

            <div className="flex justify-center mt-4">
              {renderPagination()}
            </div>
          </div>
        </div>
        </div>
      </PageComponent>
    </>
  );
}

export default Applications;